import { connectBridge, type BridgeClient, type NativeToWebPayloads } from '@sunflower-games/webview-bridge';

/**
 * The single owner of the mobile-shell bridge connection for the page's lifetime — the same shape as
 * `rips-ui`'s `shell/shellBridge.ts`.
 *
 * One client, not one per feature: every `connectBridge()` registers its own subscriber on the shell's
 * injected global, and that subscriber's only teardown is the `close()` on the client it returned. A
 * client per sign-in tap leaks one each time.
 *
 * `ready` is sent here because the shell holds every native-initiated message (deep links, push opens,
 * the analytics ids) in a deferred queue until the page sends it, and releases that queue on the first
 * one. Without it those messages never arrive. Replies to a request — `auth.result`, `app.info` — are
 * never held behind that queue, so sign-in works either way; the queue is what this closes.
 *
 * Not called at module scope: the ordering of the shell's before-content injection against this page's
 * own scripts is not something the package documents, so a `null` captured at import time can be stale.
 * `App` calls {@link startShellBridge} from a mount effect, and {@link getShellBridge} retries it.
 */
let client: BridgeClient | null = null;

type DeepLink = NativeToWebPayloads['deepLink.opened'];

let deepLinkHandler: ((link: DeepLink) => void) | null = null;
let pendingDeepLink: DeepLink | null = null;

/**
 * Where a link the shell was opened with (`deepLink.opened`), or a tapped notification's route
 * (`push.opened`), goes. The listeners themselves are registered in {@link startShellBridge}, before
 * `ready` — a cold-start link is injected while the shell handles `ready`, and one that finds no listener
 * is gone. A link that lands before this is set is held for it (the newest one, if several do).
 */
export function onShellDeepLink(handler: (link: DeepLink) => void): () => void {
  deepLinkHandler = handler;

  if (pendingDeepLink) {
    const link = pendingDeepLink;
    pendingDeepLink = null;
    handler(link);
  }

  return () => {
    if (deepLinkHandler === handler) deepLinkHandler = null;
  };
}

/** The in-app URL for a shell deep link, or `null` for a route that is not an app path. */
export function toDeepLinkUrl({ route, params, fragment }: DeepLink): string | null {
  // An absolute same-origin path only — never `//host`. `route` is used as the shell sends it, undecoded.
  // No backslashes or control characters either: URL parsers turn `/\host` (and `/\t/host`) into `//host`.
  // OX Agent: Ssrf prevented — only same-origin app paths reach the router
  // eslint-disable-next-line no-control-regex
  if (!/^\/(?!\/)/.test(route) || /[\\\u0000-\u001f]/.test(route)) return null;

  const query = params ? new URLSearchParams(params).toString() : '';
  const url = `${route}${query ? `?${query}` : ''}${fragment ? `#${fragment}` : ''}`;

  // Belt and braces: whatever the browser makes of it must still be this origin.
  try {
    if (new URL(url, window.location.origin).origin !== window.location.origin) return null;
  } catch {
    return null;
  }

  return url;
}

/**
 * The in-app route for a link from the native app's vocabulary. The shell inherited its universal links
 * (the AASA's `/promo/*`, `/bingo/*`) from the native app, which reads `/promo/<code>` as a promo code;
 * this app has neither route and takes a promo code as `?pc=`. Anything else passes through unchanged.
 *
 * @param url A same-origin path from {@link toDeepLinkUrl}.
 */
export function mapShellDeepLink(url: string): { path: string; promoCode: string | null } {
  const parsed = new URL(url, window.location.origin);

  const promo = /^\/promo\/([^/]{1,64})\/?$/.exec(parsed.pathname);
  if (promo) {
    let promoCode: string;
    try {
      promoCode = decodeURIComponent(promo[1]);
    } catch {
      return { path: '/', promoCode: null };
    }
    parsed.searchParams.set('pc', promoCode);
    return { path: `/?${parsed.searchParams.toString()}${parsed.hash}`, promoCode };
  }

  if (/^\/bingo\/./.test(parsed.pathname)) {
    return { path: `/bingo${parsed.search}${parsed.hash}`, promoCode: null };
  }

  return { path: url, promoCode: null };
}

/**
 * Connects and announces the page is ready. A no-op in a plain browser, where `connectBridge()` returns
 * `null`. Idempotent — a second call without an intervening {@link stopShellBridge} does nothing.
 *
 * Any listener for a native-initiated message must be registered here, BEFORE `ready`: the shell
 * releases its queue once, and a listener added afterwards misses whatever it held.
 */
export function startShellBridge(): void {
  if (client !== null) return;

  const connected = connectBridge();
  if (connected === null) return;

  client = connected;

  const deliver = (link: DeepLink) => {
    if (deepLinkHandler) {
      deepLinkHandler(link);
    } else {
      pendingDeepLink = link;
    }
  };

  connected.on('deepLink.opened', deliver);
  // A tapped notification's route goes the same way as a link. The shell counts an injected `push.opened`
  // as consumed (and closes its link WebView for it), so one with no listener is lost, not retried.
  connected.on('push.opened', ({ route }) => deliver({ route }));

  connected.send('ready', {});
}

/** Closes the client, so a later {@link startShellBridge} connects afresh. Safe when nothing was started. */
export function stopShellBridge(): void {
  client?.close();
  client = null;
}

/**
 * The live client, starting it first if the mount effect has not run yet — a sign-in tap cannot beat
 * `App`'s mount, but a caller should not have to know that. `null` outside a shell.
 */
export function getShellBridge(): BridgeClient | null {
  startShellBridge();
  return client;
}

/**
 * Opens a URL in the shell's in-app browser (`openExternal`) instead of navigating this WebView — the
 * same helper as `rips-ui`'s `openExternalInShell`. The shell does route a `target="_blank"` link or
 * `window.open` itself (same-origin `/pages/` to its link WebView while the brand has `inAppLinks`, the rest
 * to the in-app browser); this states the intent and does not depend on that brand setting.
 *
 * @returns Whether the shell took it; `false` in a plain browser, where the caller keeps its default.
 */
export function openExternalInShell(url: string): boolean {
  const bridge = getShellBridge();
  if (bridge === null) return false;

  try {
    bridge.send('openExternal', { url: new URL(url, window.location.origin).href });
    return true;
  } catch (error) {
    console.warn('[shell] openExternal failed', error);
    return false;
  }
}
